<script setup lang="ts">
import { watchEffect } from 'vue'
import { type ID } from '@/types/basic'
import GwTab, { type GwTabType } from '@/components/lib/GwTabs/GwTab.vue'

export interface GwTabsProps {
  options: GwTabType[]
  value?: ID
  direction?: 'vertical' | 'horizontal'
  hasActiveAutoScroll?: boolean
  disabled?: boolean
  hasDynamicSizeEmit?: boolean
  loading?: boolean
}

const props = withDefaults(defineProps<GwTabsProps>(), {
  options: () => [], // TODO add loading items
  value: '',
  direction: 'horizontal',
  hasActiveAutoScroll: true,
})

const scrollToActiveTab = () => {
  const tab = document.getElementById(`tab_${props.value}`)
  if (tab) {
    tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
  }
}

const emit = defineEmits<{
  click: [Event, GwTabType]
  'tab:mousedown': [Event, GwTabType]
  'tab:mouseup': [Event, GwTabType]
  'tab:touchstart': [Event, GwTabType]
  'tab:touchend': [Event, GwTabType]
}>()

if (props.hasActiveAutoScroll) {
  watchEffect(
    () => {
      if (props.options.length && props.value) {
        setTimeout(() => {
          requestAnimationFrame(() => scrollToActiveTab())
        })
      }
    },
    {
      flush: 'post',
    },
  )
}
</script>

<template>
  <div
    class="gwtabs d-flex f-no-wrap w-100"
    style="gap: var(--app-gap-1)"
    :class="[`gwtabs_direction_${direction}`]"
  >
    <slot name="before"></slot>
    <GwTab
      v-for="(item, index) in options"
      :id="`tab_${item.id}`"
      :key="item.id"
      :data-tst="`gw_tab_${index}`"
      :item="item"
      :hasActiveAutoScroll
      :active="value === item.id"
      :disabled
      :hasDynamicSizeEmit
      :loading
      @click="emit('click', $event, item)"
      @tab:mousedown="emit('tab:mousedown', $event, item)"
      @tab:mouseup="emit('tab:mouseup', $event, item)"
      @tab:touchstart="emit('tab:touchstart', $event, item)"
      @tab:touchend="emit('tab:touchend', $event, item)"
    >
      <slot :item="item">{{ item.name }}</slot>
    </GwTab>
    <slot name="after"></slot>
  </div>
</template>

<style lang="scss" scoped>
.gwtabs {
  position: relative;
  overflow: hidden;
  min-width: fit-content;
  z-index: 4;
}
</style>
